import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import {
  useViewModel,
  useAuth,
  useAuthIsAnonymous,
  useChatbotViewModel,
  type ChatbotState,
  type ChatbotViewModel,
} from '@footsteps/shared';
import { WozCaseManagerViewModel } from './woz-case-manager.viewmodel';
import type { WozCaseManagerState, WozCaseJSON } from './woz-case-manager.state';
import type { WozCaseState } from './woz-case/woz-case.state';
import type { WozCaseViewModel } from './woz-case/woz-case.viewmodel';
import { useWozCaseViewModel } from './woz-case/woz-case.viewmodel';
import { WozCaseChatbotStorageAdapter } from './services/storage/woz-case-chatbot-storage.adapter';

type WozCaseManagerContextValue = {
  caseManagerState: WozCaseManagerState;
  caseManagerViewModel: Omit<WozCaseManagerViewModel, 'state'>;
};

type WozCaseContextValue = {
  caseState: WozCaseState;
  caseViewModel: Omit<WozCaseViewModel, 'state'>;
  chatbotState: ChatbotState;
  chatbotViewModel: Omit<ChatbotViewModel, 'state'>;
};

const WozCaseManagerContext = createContext<WozCaseManagerContextValue | null>(null);
const WozCaseContext = createContext<WozCaseContextValue | null>(null);

const WozCaseProvider = ({ currentCase, children }: PropsWithChildren<{ currentCase: WozCaseJSON }>) => {
  const isAnonymous = useAuthIsAnonymous();

  // 1. Create the case viewmodel first (holds messages + flow in state.data).
  const [caseState, caseViewModel] = useWozCaseViewModel(currentCase);

  // 2. Create the storage adapter and connect it to the case viewmodel
  //    synchronously — before the chatbot viewmodel constructor calls restore().
  const [storageAdapter] = useState(() => {
    const adapter = new WozCaseChatbotStorageAdapter(caseViewModel as WozCaseViewModel);
    return adapter;
  });

  // 3. Create the chatbot viewmodel with the connected adapter.
  //    Its constructor will call restore() → adapter.load() → reads from case data.
  const [chatbotState, chatbotViewModel] = useChatbotViewModel(storageAdapter);
  const flowStartedRef = useRef(false);

  // Wire the case viewmodel to the chatbot viewmodel
  useEffect(() => {
    caseViewModel.setChatbot(chatbotViewModel as ChatbotViewModel);
  }, [caseViewModel, chatbotViewModel]);

  // Auto-start or resume the flow on mount.
  useEffect(() => {
    if (flowStartedRef.current) return;
    flowStartedRef.current = true;
    caseViewModel.startFlow();
  }, [caseViewModel]);

  // When an anonymous user verifies (anon → known) and the flow is already
  // complete, persist the finished case to the database. During the chatbot
  // flow itself no database writes happen — only localStorage is used.
  const wasAnonymousRef = useRef(isAnonymous);
  useEffect(() => {
    const wasAnonymous = wasAnonymousRef.current;
    wasAnonymousRef.current = isAnonymous;

    if (wasAnonymous && !isAnonymous) {
      caseViewModel.promoteToDatabase();
    }
  }, [isAnonymous, caseViewModel, caseState.isFlowComplete]);

  return createElement(
    WozCaseContext.Provider,
    { value: { caseState, caseViewModel, chatbotState, chatbotViewModel } },
    children
  );
};

export const WozCaseManagerProvider = ({ children }: { children: ReactNode }) => {
  const { user, signInAnonymously } = useAuth();
  const userId = user?.getId() ?? null;

  const [caseManagerState, caseManagerViewModel] = useViewModel<WozCaseManagerState, WozCaseManagerViewModel>(
    () => new WozCaseManagerViewModel()
  );

  useEffect(() => {
    caseManagerViewModel.setSignInAnonymously(signInAnonymously);
  }, [caseManagerViewModel, signInAnonymously]);

  useEffect(() => {
    caseManagerViewModel.setUser(user ?? null);
  }, [userId]);

  const { currentCase } = caseManagerState;

  const inner = currentCase
    ? createElement(WozCaseProvider, { key: currentCase.id ?? currentCase.createdAt, currentCase }, children)
    : children;

  return createElement(WozCaseManagerContext.Provider, { value: { caseManagerState, caseManagerViewModel } }, inner);
};

export const useWozCaseManager = () => {
  const context = useContext(WozCaseManagerContext);
  if (!context) {
    throw new Error('useWozCaseManager must be used within a WozCaseManagerProvider');
  }
  return context;
};

export const useWozCase = (): WozCaseContextValue => {
  const context = useContext(WozCaseContext);
  if (!context) {
    throw new Error('useWozCase must be used within a WozCaseProvider (i.e., when a currentCase exists)');
  }
  return context;
};
